import {
    Component,
    input,
    output,
    contentChild,
    TemplateRef,
    ChangeDetectionStrategy,
    computed, inject, contentChildren, signal, ViewChild, OnDestroy, effect,
    OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { Table, TableModule, TablePageEvent } from 'primeng/table';
import { SortEvent } from 'primeng/api';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FilterComponent } from '@client/app/shared-modules/filter/components/filter-component/filter.component';
import {
    FilterDisplayComponent,
} from '@client/app/shared-modules/filter/components/filter-display-component/filter-display.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { FilterObject, FilterResponse } from '@client/app/shared-modules/filter/types/filter.type';
import {
    GridKeyboardNavigationDirective,
} from '@client/app/shared-modules/table/directives/grid-keyboard-navigation.directive';
import { AVAILABLE_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { CreateExport } from '@client/app/shared-modules/create-export/types/create-export.type';
import {
    CreateExportDialogService,
} from '@client/app/shared-modules/create-export/services/create-export-dialog.service';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { FormsModule } from '@angular/forms';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';
import { MyExportsDialogService } from '@client/app/shared-modules/my-exports/services/my-exports-dialog.service';
import { DialogService } from 'primeng/dynamicdialog';
import { TooltipModule } from 'primeng/tooltip';
import {
    ColumnSelectorComponent,
} from '@client/app/shared-modules/table/components/column-selector/column-selector.component';
import { SvgIconComponent } from '@client/app/shared-modules/svg-icons/components/svg-icon/svg-icon.component';
import {
    TableFocusManagementService,
    TableActionPredicate,
} from '@client/app/shared-modules/table/services/table-focus-management.service';

export type TableColumnWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export type TableWrapperHeader<T> = {
    label: string;
    secondaryLabel?: string | null;
    id: keyof T & string;
    type: 'property';
    widthClass?: string;
    widthStyle?: string;
    width?: TableColumnWidth;
    tooltip?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    sortable?: boolean;
    sortField?: string;
    actionColumn?: boolean;
    cellClass?: string;
    defaultHidden?: boolean;
    alwaysVisible?: boolean;
} | {
    label: string;
    secondaryLabel?: string | null;
    id: string;
    type: 'custom';
    widthClass?: string;
    widthStyle?: string;
    width?: TableColumnWidth;
    tooltip?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    sortable?: boolean;
    sortField?: string;
    actionColumn?: boolean;
    cellClass?: string;
    defaultHidden?: boolean;
    alwaysVisible?: boolean;
};

@Component({
    selector: 'mon-table-wrapper',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        Button,
        FilterComponent,
        FilterDisplayComponent,
        TranslocoModule,
        IconField,
        InputIcon,
        InputText,
        GridKeyboardNavigationDirective,
        ColumnSelectorComponent,
        FormsModule,
        CheckboxModule,
        TooltipModule,
        SvgIconComponent,
    ],
    providers: [
        DialogService,
        MyExportsDialogService,
        CreateExportDialogService,
        TableFocusManagementService,
    ],
    templateUrl: './table-wrapper.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./table-wrapper.component.scss'],
})
export class TableWrapperComponent<T> implements OnInit, OnDestroy {
    private readonly widthClassMap: Record<TableColumnWidth, string> = {
        xs: 'tw-w-20',
        sm: 'tw-w-28',
        md: 'tw-w-40',
        lg: 'tw-w-56',
        xl: 'tw-w-64',
        xxl: 'tw-w-96',
    };

    tableKey = input.required<string>();
    value = input.required<T[]>();
    headers = input.required<TableWrapperHeader<T>[]>();
    total = input<number>(0);
    currentPage = input<number>(1);
    perPage = input<number>(AVAILABLE_PAGE_SIZE[0]);
    showPageLinks = input<boolean>(true);
    showFirstLastIcon = input<boolean>(true);

    first = computed(() => {
        return ((this.currentPage() || 1) - 1) * this.perPage();
    });

    loading = input<boolean>(false);
    dataKey = input<string>('id');
    selection = input<T[]>([]);
    showSelectionColumn = input<boolean>(false);
    selectionDisabled = input<boolean>(false);
    headerCheckboxChecked = input<boolean>(false);
    headerCheckboxIndeterminate = input<boolean>(false);
    sortBy = input<string | undefined>(undefined);
    sortDir = input<string>('');

    sortOrder = computed(() => {
        if (this.sortDir() === 'asc') {
            return 1;
        }
        if (this.sortDir() === 'desc') {
            return -1;
        }
        return 0;
    });
    sortField = computed(() => {
        if (this.sortOrder() === 0) {
            return undefined;
        }
        return this.sortBy();
    });

    searchText = input<string | undefined>();
    showSearch = computed(() => {
        return this.searchText() !== undefined;
    });
    // Filters
    filters = input<FilterObject | null>(null);
    appliedFilters = input<FilterResponse>({});
    filterOrder = input<string[]>([]);
    filterPageKey = input<string | null>(null);

    // Columns
    showCustomizeColumns = input(false);
    selectedColumns = signal<TableWrapperHeader<T>[]>([]);
    private previousHeaderIds = new Set<string>();
    selectableColumns = computed(() => this.headers().filter(h => !h.alwaysVisible));
    displayedColumns = computed(() => {
        if (this.showCustomizeColumns()) {
            const selected = this.selectedColumns();
            return this.headers().filter(h => h.alwaysVisible || selected.some(s => s.id === h.id));
        }

        return this.headers();
    });
    sortedSelectedColumns = computed(() => {
        const headerOrder = this.headers();
        const selected = this.selectedColumns();

        return selected.sort((a, b) => {
            const indexA = headerOrder.findIndex(h => h.id === a.id);
            const indexB = headerOrder.findIndex(h => h.id === b.id);
            return indexA - indexB;
        });
    });

    // Exports
    exportConfig = input<CreateExport | null>(null);

    // Text
    searchPlaceholder = input<string>('');
    searchAriaDescribedBy = input<string | null>(null);
    searchResultsAnnouncement = input<string | null>(null);
    exportButtonLabel = input<string>('');
    emptySearchMessage = input('');
    emptyMessage = input('');

    fixedTableLayout = input<boolean>(false);

    sort = output<SortEvent>();
    page = output<TablePageEvent>();
    searchInput = output<Event>();
    selectionChange = output<T[]>();
    headerCheckboxToggle = output<void>();
    filtersApplied = output<FilterResponse>();
    loadMoreFilterOptions = output<{ filterKey: string, page: number }>();

    headerTemplate = contentChild<TemplateRef<any>>('header', { descendants: true });
    cellsTemplate = contentChild<TemplateRef<any>>('cells', { descendants: true });
    cellTemplates = contentChildren(TableCellTemplateDirective<T>);

    actionTriggerService = inject(ActionTriggerService);
    createExportDialogService = inject(CreateExportDialogService);
    readonly focusService = inject(TableFocusManagementService);

    protected readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;
    @ViewChild(Table) private readonly tableRef?: Table;

    constructor () {
        effect(() => {
            const currentHeaders = this.headers();
            const currentIds = new Set(currentHeaders.map(h => h.id));

            const newHeaders = currentHeaders.filter(
                h => !this.previousHeaderIds.has(h.id) && !h.defaultHidden && !h.alwaysVisible,
            );
            const removedIds = [...this.previousHeaderIds].filter(id => !currentIds.has(id));

            if (newHeaders.length > 0 || removedIds.length > 0) {
                this.selectedColumns.update(prev => [
                    ...prev.filter(c => currentIds.has(c.id)),
                    ...newHeaders,
                ]);
            }

            this.previousHeaderIds = currentIds;

            this.value(); // subscribe to value signal
            if (this.tableRef) {
                this.focusService.handlePostReloadFocus(this.tableRef);
            }
        });
    }


    static createTableSchema<
        T,
        S = Record<string, TableWrapperHeader<T>>,
    > (schema: S): S {
        return schema;
    }

    ngOnInit (): void {
        this.selectedColumns.set(this.headers());
    }

    ngOnDestroy (): void {
        this.focusService.reset();
    }

    /**
     * Track a row action for focus restoration.
     * Call this before performing an action that may remove a row (delete, resolve, dismiss, etc.).
     * Focus will automatically be restored to the correct row after the table's value input updates.
     *
     * @param items - The current list of items in the table
     * @param targetItem - The item being acted upon
     * @param action - The action being performed
     * @param willRemoveRowPredicate - Returns true if this action will remove the row from the table
     *
     * @example
     * // In page component:
     * // @ViewChild(TableWrapperComponent) tableWrapper!: TableWrapperComponent<MyItem>;
     * // this.tableWrapper.trackAction(this.items, item, action, a => a === 'delete');
     */
    trackAction<TItem, TAction>(
        items: TItem[],
        targetItem: TItem,
        action: TAction,
        willRemoveRowPredicate: TableActionPredicate<TAction>,
    ): void {
        this.focusService.handleTableAction(items, targetItem, action, willRemoveRowPredicate);
    }

    handleSort (event: SortEvent): void {
        this.sort.emit(event);
    }

    handleSortClick (event: MouseEvent, field: string): void {
        this.tableRef?.sort({ originalEvent: event, field });
    }

    handlePage (event: TablePageEvent): void {
        this.page.emit(event);
    }

    handleSelectionChange (selection: T[]): void {
        this.selectionChange.emit(selection);
    }

    handleHeaderCheckboxToggle (): void {
        this.headerCheckboxToggle.emit();
    }

    openCreateExport (): void {
        this.actionTriggerService.track({ action: this.tableKey() + '-create-export-btn' });

        const config = this.exportConfig();
        if (!config) {
            return;
        }

        this.createExportDialogService.open(config);
    }

    getColumnWidthClass (column: TableWrapperHeader<T>): string {
        if (column.widthClass) {
            return column.widthClass;
        }

        if (column.width) {
            return this.widthClassMap[column.width];
        }

        if (column.actionColumn) {
            return this.widthClassMap[column.width ?? 'sm'];
        }

        return '';
    }

    getCellTemplate (id: string): TemplateRef<any> | null {
        return this.cellTemplates().find(t => t.header.id === id)?.template || null;
    }
}
